import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectTimelineAssetForMatter } from '@/timeline/asset/store/selectors'
import TimelineAssetChatButton from '@/timeline/asset/components/TimelineAssetChatButton'
import TimelineAssetDownloadButton from '@/timeline/asset/components/TimelineAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Timeline: Matter > Timeline, with Ask Assistant + Download
 * actions once the timeline has events.
 */
export default function MatterBreadcrumbsTimelineAsset({ showAddFilesCrumb = false }: { showAddFilesCrumb?: boolean } = {}) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()

  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const asset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'timeline',
      label: 'Timeline',
      // On the Add Files sub-page, Timeline becomes a clickable parent crumb.
      ...(showAddFilesCrumb && matterId ? { onClick: () => navigate(matterPaths.timeline.index(matterId)) } : {}),
    })
    if (showAddFilesCrumb) {
      items.push({ id: 'add-files', label: 'Add Files' })
    }
    return items
  }, [initialLoadComplete, matter, navigate, showAddFilesCrumb, matterId])

  const showActions = asset && asset.total_events > 0 && matterId

  return (
    // Negative margin is to make this match the design files / chat (due to sidebar closing button gap)
    <div className="h-[52px] lg:h-[60px] flex lg:-ml-3">
      <div className="w-full px-3 lg:pr-6 flex justify-between items-center border-b-[0.5px] bg-brand-neutral-100 lg:bg-brand-neutral-50">
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumbs items={breadcrumbItems} maxLabelLength={30} maxActiveLabelLength={60} persistFirstItem />
        </div>

        {showActions && (
          <div className="hidden lg:flex items-center justify-center gap-3.5">
            <TimelineAssetChatButton assetId={asset.id} matterId={matterId} />
            <TimelineAssetDownloadButton assetId={asset.id} />
          </div>
        )}
      </div>
    </div>
  )
}
